'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { Check, Search, Slash } from 'lucide-react'
import { useMediaQuery } from '@/hooks/use-media-query'

/**
 * The auth screen's left panel: the product's one defining action, performed on
 * a loop. A name types into a real search field, `.mn` completes it, and the
 * registry answers.
 *
 * Availability is coded by lightness + glyph, never hue alone - available is
 * `--success` with a check, taken is muted with a slash. Taken is information,
 * not an error, so it deliberately avoids `--danger` red, and the pair stays
 * readable under red-green colour blindness.
 */

const DOMAINS = [
  { name: 'mandal', available: true },
  { name: 'urgoo', available: false },
  { name: 'altan-shop', available: true },
  { name: 'sondor', available: false },
  { name: 'tsatsral', available: true },
]

type Phase = 'typing' | 'checking' | 'result' | 'clearing'

const TYPE_MS = 75      // per character
const SETTLE_MS = 240   // beat after the last character
const CHECK_MS = 720    // querying the registry
const HOLD_MS = 1900    // answer on screen
const CLEAR_MS = 380    // field empties

// Every discrete transition stays under 200ms; only the typing cadence is slower,
// and that is content rather than decoration.
const EASE = { duration: 0.16, ease: 'easeOut' } as const

export default function DomainShowcase() {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const reduceMotion = useReducedMotion()

  // Hidden below lg, or reduced motion: hold one composed frame rather than
  // burning timers on an invisible loop.
  const isStatic = !isDesktop || !!reduceMotion

  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('typing')
  const [typed, setTyped] = useState('')

  const current = DOMAINS[index]

  useEffect(() => {
    if (isStatic) return
    let timer: NodeJS.Timeout

    if (phase === 'typing') {
      if (typed.length < current.name.length) {
        timer = setTimeout(() => setTyped(current.name.slice(0, typed.length + 1)), TYPE_MS)
      } else {
        timer = setTimeout(() => setPhase('checking'), SETTLE_MS)
      }
    } else if (phase === 'checking') {
      timer = setTimeout(() => setPhase('result'), CHECK_MS)
    } else if (phase === 'result') {
      timer = setTimeout(() => setPhase('clearing'), HOLD_MS)
    } else {
      timer = setTimeout(() => {
        setIndex(i => (i + 1) % DOMAINS.length)
        setTyped('')
        setPhase('typing')
      }, CLEAR_MS)
    }

    return () => clearTimeout(timer)
  }, [isStatic, phase, typed, current.name])

  const shownName = isStatic ? current.name : typed
  const shownPhase: Phase = isStatic ? 'result' : phase
  const isComplete = shownPhase === 'checking' || shownPhase === 'result'

  return (
    <div className="w-full h-full bg-muted flex items-center justify-center p-10">
      <div className="w-full max-w-md flex flex-col items-center gap-8">

        {/* Illustrative, not interactive - the copy below carries the meaning */}
        <div aria-hidden className="w-full flex flex-col items-center gap-4">

          {/* The search field. Same surface, border and radius as a real one. */}
          <div className="w-full h-14 flex items-center gap-3 px-4 bg-pure border border-border rounded-xl">
            <Search className="size-[18px] shrink-0 text-muted-foreground" strokeWidth={2} />

            <div className="flex-1 flex items-center min-w-0 font-medium text-lg tracking-tight">
              <span className="text-foreground">{shownName}</span>

              { !isComplete && (
                <motion.span
                  className="inline-block w-px h-[1.15em] ml-px bg-foreground"
                  animate={{ opacity: [1, 1, 0, 0] }}
                  transition={{ duration: 1, repeat: Infinity, times: [0, 0.5, 0.5, 1], ease: 'linear' }}
                />
              ) }

              <AnimatePresence>
                { isComplete && (
                  <motion.span
                    key="tld"
                    className="text-blue"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={EASE}
                  >
                    .mn
                  </motion.span>
                ) }
              </AnimatePresence>
            </div>

            {/* Verdict sits in the field, where a registrar puts it */}
            <div className="size-5 shrink-0 flex items-center justify-center">
              <AnimatePresence mode="wait">
                { shownPhase === 'checking' && (
                  <motion.span
                    key="pending"
                    className="size-1.5 rounded-full bg-muted-foreground"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut' }}
                  />
                ) }
                { shownPhase === 'result' && (
                  <motion.span
                    key={`v-${index}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={EASE}
                  >
                    { current.available
                      ? <Check className="size-[18px] text-success" strokeWidth={2.5} />
                      : <Slash className="size-[18px] text-muted-foreground" strokeWidth={2.5} /> }
                  </motion.span>
                ) }
              </AnimatePresence>
            </div>
          </div>

          {/* Answer */}
          <div className="h-5">
            <AnimatePresence mode="wait">
              { shownPhase === 'result' && (
                <motion.p
                  key={`s-${index}`}
                  className={`font-gilroy text-sm font-semibold ${current.available ? 'text-success' : 'text-muted-foreground'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={EASE}
                >
                  { current.available ? 'Боломжтой' : 'Эзэмшсэн' }
                </motion.p>
              ) }
            </AnimatePresence>
          </div>
        </div>

        <p className="max-w-sm text-center text-sm leading-6 text-muted-foreground">
          Домэйнээ шалгаад бүртгүүлж, DNS тохиргоогоо нэг дороос удирдаарай.
        </p>
      </div>
    </div>
  )
}
